import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, HandHeart } from "lucide-react";
import api, { apiError } from "@/api";
import { toast } from "sonner";

export default function PledgeDialog() {
  const { user, setUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const show = user?.role === "coordinator" && !user?.pledge_accepted;
  if (!show) return null;

  const accept = async () => {
    if (!checked) return toast.error("يرجى الموافقة على التعهد للمتابعة");
    setLoading(true);
    try {
      await api.post("/auth/accept-pledge");
      setUser({ ...user, pledge_accepted: true });
      toast.success("تم توثيق التعهد. مرحباً بك");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" dir="rtl" data-testid="pledge-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-lg glass rounded-3xl p-8 sky-glow"
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-16 h-16 rounded-3xl icon-sky flex items-center justify-center mb-4">
            <ShieldCheck size={32} />
          </div>
          <h2 className="text-xl font-head font-bold gold-text">تعهّد والتزام منسق التطوع</h2>
          <p className="text-sm text-stone-500 mt-1">مرحباً {user?.name} — يرجى قراءة التعهّد والموافقة عليه قبل الدخول</p>
        </div>

        <div className="rounded-2xl bg-amber-50/70 border border-amber-400/30 p-5 text-stone-700 leading-relaxed text-sm space-y-3 max-h-64 overflow-y-auto">
          <p className="flex gap-2"><HandHeart size={18} className="text-sky-600 shrink-0 mt-0.5" />
            أتعهّد أنا منسق التطوع بأن جميع البيانات التي أُدخلها في النظام — من فرص تطوعية، ومتطوعين، وساعات، وأنشطة، وصور، وإحصاءات — <strong>صحيحة ومطابقة للواقع الفعلي في المدرسة</strong>.
          </p>
          <p>
            كما أُقرّ بأنني أتحمّل كامل المسؤولية عن دقّة هذه البيانات، وأنّ أي إدخال غير صحيح أو مخالف للواقع يُعرّضني <strong>للمساءلة من الجهات المختصة</strong> وفق الأنظمة المعتمدة.
          </p>
        </div>

        <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="pledge-checkbox-label">
          <input
            type="checkbox"
            data-testid="pledge-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 w-5 h-5 accent-sky-600 shrink-0"
          />
          <span className="text-sm text-stone-700">أقرّ بأنني قرأت التعهّد أعلاه وأوافق عليه والتزم بمضمونه بصدق ومسؤولية.</span>
        </label>

        <button
          data-testid="pledge-accept-btn"
          onClick={accept}
          disabled={loading || !checked}
          className="btn-gold w-full py-3 rounded-xl mt-6 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "جارٍ التوثيق..." : <><ShieldCheck size={20} /> أوافق وأدخل النظام</>}
        </button>
      </motion.div>
    </div>
  );
}
